import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PersonalSpacesRow, PersonalSpacesSource } from "@/api/admin-api"

import {
  downloadPersonalSpacesCsv,
  getPersonalSpacesPurgeBatches,
  getPersonalSpacesSettings,
  getPersonalSpacesSummary,
  pushPersonalSpacesPolicy,
} from "@/api/admin-api"
import { Download, RefreshCw, Shield } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { getApiErrorMessage } from "@/lib/api-error-message"

import { AdminDashboardError } from "./admin-dashboard-error"
import { LoadingCard } from "./admin-dashboard-loading"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { PersonalPolicyConfirmDialog } from "./personal-policy-dialogs"
import { PersonalPurgeBatches } from "./personal-purge-batches"
import {
  filterPersonalSpacesRows,
  formatPersonalSpacesBytes,
  formatPersonalSpacesDuration,
  formatPersonalSpacesLag,
  PERSONAL_SPACES_PAGE_SIZE,
  PERSONAL_SPACES_SOURCES,
  personalSpacesCsvFilename,
  personalSpacesSkipEntries,
  personalSpacesStatusVariant,
} from "./personal-sources-card-utils"
import { adminQueryKeys } from "../hooks/admin-query-keys"
import { usePersonalPolicyConfirmation } from "../hooks/use-personal-policy-confirmation"

const ALL_SOURCES = "all"

/**
 * Admin card for personal sources (slice 7H): per user and per source the item
 * count, storage, index time, status, skip counters per reason and freshness lag,
 * with totals per source and a CSV export. It shows counts and statuses only; no
 * content of any user reaches this card (PS-11, PS-15).
 */
export function PersonalSourcesCard() {
  const { t } = useTranslation()
  const [source, setSource] = useState<PersonalSpacesSource | typeof ALL_SOURCES>(ALL_SOURCES)
  const [query, setQuery] = useState("")
  const [shown, setShown] = useState(PERSONAL_SPACES_PAGE_SIZE)
  const [exporting, setExporting] = useState(false)
  const selected = source === ALL_SOURCES ? undefined : source

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: adminQueryKeys.personalSpacesSummary(source),
    queryFn: () => getPersonalSpacesSummary(selected),
    staleTime: 30_000,
  })

  // Held mass-purge batches and the current settings per source (slice 8H). Neither blocks the
  // card: if they cannot be read, the counts above still render and the sections stay out.
  const available = data?.available === true
  const batches = useQuery({
    queryKey: adminQueryKeys.personalSpacesBatches(),
    queryFn: getPersonalSpacesPurgeBatches,
    enabled: available,
    staleTime: 30_000,
    retry: false,
  })
  const settings = useQuery({
    queryKey: adminQueryKeys.personalSpacesSettings(),
    queryFn: getPersonalSpacesSettings,
    enabled: available,
    staleTime: 30_000,
    retry: false,
  })
  const policy = usePersonalPolicyConfirmation()
  const [reviewing, setReviewing] = useState(false)
  const changeWaiting = settings.data?.policy?.last_status === "confirmation_required"

  // Asks FILTH again where the held change stands: that answer carries the count to confirm.
  const reviewHeldChange = async () => {
    setReviewing(true)
    try {
      const { policy_push: push } = await pushPersonalSpacesPolicy()
      policy.reportPush(push)
      if (push.status === "applied") void settings.refetch()
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err,
          t(
            "adminDashboard.personalSources.policy.confirmFailed",
            "The change could not be applied"
          )
        )
      )
    } finally {
      setReviewing(false)
    }
  }

  const title = t("adminDashboard.personalSources.title", "Personal sources")
  const sourceLabel = (name: PersonalSpacesSource) =>
    t(`adminDashboard.personalSources.source.${name}`)

  const handleExport = async () => {
    setExporting(true)
    try {
      const blob = await downloadPersonalSpacesCsv(selected)
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = personalSpacesCsvFilename(selected, new Date())
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err,
          t("adminDashboard.personalSources.exportFailed", "The export could not be created")
        )
      )
    } finally {
      setExporting(false)
    }
  }

  const rows = filterPersonalSpacesRows(data?.rows ?? [], query)
  const visible = rows.slice(0, shown)

  return (
    <CollapsibleManagementSection
      icon={<Shield className="size-5" />}
      id="personal-sources"
      storageKey="admin-management-personal-sources-expanded"
      title={title}
      headerActions={
        data?.available ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              aria-label={t("adminDashboard.personalSources.refresh", "Refresh")}
              disabled={isFetching}
              size="icon"
              variant="ghost"
              onClick={() => void refetch()}
            >
              <RefreshCw className="size-4" />
            </Button>
            <Button
              disabled={exporting}
              size="sm"
              variant="outline"
              onClick={() => void handleExport()}
            >
              <Download className="mr-2 size-4" />
              {t("adminDashboard.personalSources.export", "Export CSV")}
            </Button>
          </div>
        ) : undefined
      }
    >
      {isLoading && <LoadingCard title={title} />}
      {!isLoading && error && <AdminDashboardError error={error} onRetry={() => void refetch()} />}
      {!isLoading && !error && data && !data.available && (
        <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
          {t(
            "adminDashboard.personalSources.unavailable",
            "Personal sources are not turned on for this environment, or the connected Prism does not support them."
          )}
        </p>
      )}
      {!isLoading && !error && data?.available && (
        <div className="space-y-6">
          <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
            {t(
              "adminDashboard.personalSources.description",
              "Counts per user and source. Nothing here shows what anyone has connected or written."
            )}
          </p>

          {changeWaiting && (
            <div
              className="bg-warning text-warning-foreground flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm"
              data-testid="personal-policy-waiting"
              role="status"
            >
              <span>
                {t(
                  "adminDashboard.personalSources.policy.waiting",
                  "A saved change is waiting for your confirmation. Prism keeps enforcing the previous settings until then."
                )}
              </span>
              <Button
                disabled={reviewing}
                size="sm"
                variant="outline"
                onClick={() => void reviewHeldChange()}
              >
                {t("adminDashboard.personalSources.policy.review", "Review")}
              </Button>
            </div>
          )}

          <PersonalPurgeBatches batches={batches.data?.batches ?? []} />

          <dl className="grid gap-4 sm:grid-cols-3" data-testid="personal-sources-totals">
            {PERSONAL_SPACES_SOURCES.map((name) => {
              const totals = data.totals[name]
              if (!totals) return null
              const skipped = Object.values(totals.skips_by_reason).reduce((a, b) => a + b, 0)
              return (
                <div key={name} className="space-y-1 rounded-md border p-4">
                  <dt className="text-sm font-semibold">{sourceLabel(name)}</dt>
                  <dd className="text-muted-foreground space-y-0.5 text-xs tabular-nums">
                    <div>
                      {t("adminDashboard.personalSources.totals.users", "{{count}} users", {
                        count: totals.users,
                      })}
                      {" · "}
                      {t("adminDashboard.personalSources.totals.items", "{{count}} items", {
                        count: totals.items,
                      })}
                    </div>
                    <div>
                      {formatPersonalSpacesBytes(totals.storage_bytes)}
                      {" · "}
                      {formatPersonalSpacesDuration(totals.index_seconds)}
                    </div>
                    <div>
                      {t("adminDashboard.personalSources.totals.skipped", "{{count}} skipped", {
                        count: skipped,
                      })}
                    </div>
                    {settings.data?.sources[name] && (
                      <div>
                        {t(
                          "adminDashboard.personalSources.totals.window",
                          "History window: {{count}} days",
                          { count: settings.data.sources[name].history_window_days }
                        )}
                      </div>
                    )}
                    {totals.reconnect_required > 0 && (
                      <div>
                        {t(
                          "adminDashboard.personalSources.totals.reconnect",
                          "{{count}} need to reconnect",
                          { count: totals.reconnect_required }
                        )}
                      </div>
                    )}
                    {totals.freshness_alerts > 0 && (
                      <div>
                        {t(
                          "adminDashboard.personalSources.totals.behind",
                          "{{count}} more than 90 minutes behind",
                          { count: totals.freshness_alerts }
                        )}
                      </div>
                    )}
                  </dd>
                </div>
              )
            })}
          </dl>

          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={source}
              onValueChange={(value) => {
                setSource(value as PersonalSpacesSource | typeof ALL_SOURCES)
                setShown(PERSONAL_SPACES_PAGE_SIZE)
              }}
            >
              <SelectTrigger
                aria-label={t("adminDashboard.personalSources.col.source", "Source")}
                className="w-44"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SOURCES}>
                  {t("adminDashboard.personalSources.allSources", "All sources")}
                </SelectItem>
                {PERSONAL_SPACES_SOURCES.map((name) => (
                  <SelectItem key={name} value={name}>
                    {sourceLabel(name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              aria-label={t("adminDashboard.personalSources.search", "Search users")}
              className="w-64"
              placeholder={t("adminDashboard.personalSources.search", "Search users")}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setShown(PERSONAL_SPACES_PAGE_SIZE)
              }}
            />
          </div>

          {data.truncated && (
            <p className="text-sm" role="status">
              {t(
                "adminDashboard.personalSources.truncated",
                "Only the first connections are listed. Filter by source to see the rest."
              )}
            </p>
          )}
          {data.dropped_rows > 0 && (
            <p className="text-sm" role="status">
              {t(
                "adminDashboard.personalSources.dropped",
                "{{count}} rows could not be read and are left out.",
                { count: data.dropped_rows }
              )}
            </p>
          )}

          <Table className="[&_td]:px-4 [&_td]:py-3 [&_th]:px-4 [&_th]:py-3">
            <TableHeader>
              <TableRow>
                <TableHead>{t("adminDashboard.personalSources.col.user", "User")}</TableHead>
                <TableHead>{t("adminDashboard.personalSources.col.source", "Source")}</TableHead>
                <TableHead>{t("adminDashboard.personalSources.col.status", "Status")}</TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  {t("adminDashboard.personalSources.col.items", "Items")}
                </TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  {t("adminDashboard.personalSources.col.storage", "Storage")}
                </TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  {t("adminDashboard.personalSources.col.indexTime", "Index time")}
                </TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  {t("adminDashboard.personalSources.col.freshness", "Behind by")}
                </TableHead>
                <TableHead>{t("adminDashboard.personalSources.col.skipped", "Skipped")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell className="text-muted-foreground text-center" colSpan={8}>
                    {t(
                      "adminDashboard.personalSources.empty",
                      "No personal source connections yet."
                    )}
                  </TableCell>
                </TableRow>
              )}
              {visible.map((row) => (
                <PersonalSourceRow key={`${row.user_id}:${row.source}`} row={row} />
              ))}
            </TableBody>
          </Table>
          {rows.length > shown && (
            <Button
              variant="outline"
              onClick={() => setShown((current) => current + PERSONAL_SPACES_PAGE_SIZE)}
            >
              {t("adminDashboard.personalSources.showMore", "Show more")}
            </Button>
          )}
        </div>
      )}
      <PersonalPolicyConfirmDialog
        state={policy.state}
        onConfirm={() => void policy.confirm()}
        onDismiss={policy.dismiss}
      />
    </CollapsibleManagementSection>
  )
}

function PersonalSourceRow({ row }: { row: PersonalSpacesRow }) {
  const { t } = useTranslation()
  const skips = personalSpacesSkipEntries(row.skips_by_reason)
  const lag = formatPersonalSpacesLag(row.freshness_p95_seconds)
  return (
    <TableRow>
      <TableCell className="max-w-56 break-words whitespace-normal">
        <div className="font-medium">{row.display_name || row.user_id}</div>
        {row.display_name && (
          <div className="text-muted-foreground font-mono text-xs break-all">{row.user_id}</div>
        )}
      </TableCell>
      <TableCell>{t(`adminDashboard.personalSources.source.${row.source}`)}</TableCell>
      <TableCell>
        <Badge variant={personalSpacesStatusVariant(row.status)}>
          {t(`adminDashboard.personalSources.status.${row.status}`)}
        </Badge>
      </TableCell>
      <TableCell className="text-right whitespace-nowrap tabular-nums">{row.items}</TableCell>
      <TableCell className="text-right whitespace-nowrap tabular-nums">
        {formatPersonalSpacesBytes(row.storage_bytes)}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap tabular-nums">
        {formatPersonalSpacesDuration(row.index_seconds)}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap tabular-nums">
        {lag}
        {row.freshness_alert && (
          <Badge className="ml-2" variant="warning">
            {t("adminDashboard.personalSources.behindBadge", "Behind")}
          </Badge>
        )}
      </TableCell>
      <TableCell className="whitespace-normal">
        {skips.length === 0 ? (
          <span className="text-muted-foreground">0</span>
        ) : (
          <ul
            aria-label={t("adminDashboard.personalSources.col.skipped", "Skipped")}
            className="space-y-0.5 text-xs tabular-nums"
          >
            {skips.map(({ reason, count }) => (
              <li key={reason}>
                {t(`adminDashboard.personalSources.skip.${reason}`)}: {count}
              </li>
            ))}
          </ul>
        )}
      </TableCell>
    </TableRow>
  )
}
